/**
 * Types for Calendar Event Files/Media Management
 * Storage limit: 10 MB per event, images only
 */

// ====================
// DTOs
// ====================

/**
 * Basic file info returned in list views
 */
export interface CalendarEventMediaFileDto {
  id: string
  fileName: string
  displayName: string | null
  description: string | null
  mimeType: string
  fileSize: number
  fileUrl: string
  thumbnailUrl: string | null
  width: number | null
  height: number | null
  createdDate: string
  modifiedDate: string
}

/**
 * Detailed file info including full metadata
 */
export interface CalendarEventMediaFileDetailDto extends CalendarEventMediaFileDto {
  eventId: string
  uploadedBy: string
  uploadedByName: string
  tags: string[]
}

/**
 * Result of a file upload
 */
export interface CalendarEventMediaUploadResultDto {
  file: CalendarEventMediaFileDto
  url: string
  thumbnailUrl: string | null
}

/**
 * Storage quota information
 */
export interface CalendarEventStorageQuotaDto {
  eventId: string
  usedBytes: number
  limitBytes: number
  remainingBytes: number
  usedPercentage: number
  fileCount: number
}

/**
 * Result of selecting/linking a file to an event
 */
export interface CalendarEventFileSelectionResultDto {
  success: boolean
  file: CalendarEventMediaFileDto
  message?: string
}

// ====================
// REQUEST TYPES
// ====================

/**
 * Parameters for listing event files
 */
export interface GetCalendarEventFilesParams {
  page?: number
  pageSize?: number
  searchTerm?: string
  sortBy?: 'name' | 'date' | 'size'
  sortDesc?: boolean
}

/**
 * Request to update file metadata
 */
export interface UpdateCalendarEventMediaFileRequest {
  displayName?: string
  description?: string
  tags?: string[]
}

/**
 * Request to set cover image
 */
export interface SetCalendarEventCoverImageRequest {
  mediaFileId: string | null
}

/**
 * Options for file upload
 */
export interface CalendarEventFileUploadOptions {
  displayName?: string
  description?: string
  tags?: string[]
}

// ====================
// RESPONSE TYPES
// ====================

/**
 * Paginated response for file listing
 */
export interface CalendarEventFileListResponse {
  files: CalendarEventMediaFileDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// ====================
// CONSTANTS
// ====================

/**
 * Storage limit in bytes (10 MB)
 */
export const EVENT_STORAGE_LIMIT_BYTES = 10 * 1024 * 1024

/**
 * Allowed MIME types for event images
 */
export const ALLOWED_EVENT_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const

/**
 * Allowed file extensions
 */
export const ALLOWED_EVENT_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'] as const

// ====================
// UTILITY FUNCTIONS
// ====================

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

/**
 * Check if a MIME type is allowed for event images
 */
export function isAllowedEventImageType(mimeType: string): boolean {
  return (ALLOWED_EVENT_IMAGE_TYPES as readonly string[]).includes(mimeType.toLowerCase())
}

/**
 * Check if a file extension is allowed for event images
 */
export function isAllowedEventImageExtension(fileName: string): boolean {
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
  return (ALLOWED_EVENT_IMAGE_EXTENSIONS as readonly string[]).includes(ext)
}

/**
 * Calculate remaining storage
 */
export function calculateRemainingStorage(usedBytes: number): number {
  return Math.max(0, EVENT_STORAGE_LIMIT_BYTES - usedBytes)
}

/**
 * Calculate storage percentage used
 */
export function calculateStoragePercentage(usedBytes: number): number {
  return Math.min(100, (usedBytes / EVENT_STORAGE_LIMIT_BYTES) * 100)
}
